import { useEffect, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAdminBadgeMessages, useResetBadgeMessages, useSaveBadgeMessages,
} from '../../hooks/useQuestAdmin';
import { fmtDateTime } from './questShared';
import {
  BTN, BTN_SECONDARY, Card, ErrorNote, INPUT, LABEL, Loading,
} from './adminUi';

// What each badge's reveal says in the FSM app: the word that pops up (one of
// Nano's values) and the congratulations under it. Each badge has a few
// versions; everyone who earns it is dealt one at random, so it feels a
// little more their own. The tech server holds the defaults and checks the
// limits (questSettingsService.js); these are the same limits, shown early.

const BADGES = [
  ['quest_champion', 'Quest Champion', 'Complete 10 Quests'],
  ['fibre_expert', 'Fibre Expert', 'Score 100% on three Fibre Challenges'],
  ['values_champion', 'Values Champion', 'Receive five Values in Action nominations'],
  ['quest_streak', 'Quest Streak', 'Take part three months in a row'],
  ['nano_legend', 'Nano Legend', 'Reach 5,000 points'],
];
const WORD_MAX = 16;
const MESSAGE_MAX = 220;
const MESSAGES_MAX = 8;
const SAMPLE_NAME = 'Alex';

const preview = (text) => text.replace(/\{name\}/g, SAMPLE_NAME);

function BadgeMessages({ badgeKey, title, how, data }) {
  const save = useSaveBadgeMessages();
  const reset = useResetBadgeMessages();
  const [word, setWord] = useState(data.word);
  const [messages, setMessages] = useState(data.messages);
  // take in what the server has whenever it changes (after a save or a reset)
  useEffect(() => { setWord(data.word); setMessages(data.messages); }, [data.word, data.messages]);

  const changed = word !== data.word || JSON.stringify(messages) !== JSON.stringify(data.messages);
  const setMessage = (i, text) => setMessages((m) => m.map((x, j) => (j === i ? text : x)));
  const tooLong = word.length > WORD_MAX || messages.some((m) => m.length > MESSAGE_MAX);
  const empty = !word.trim() || !messages.some((m) => m.trim());

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{how}</p>
        </div>
        <div className="text-right">
          <span className="inline-block rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
            Value: {data.value}
          </span>
          <p className="mt-1 text-[11px] text-gray-400">
            {data.customised
              ? `Edited${data.updated_by_name ? ` by ${data.updated_by_name}` : ''}${data.updated_at ? ` on ${fmtDateTime(data.updated_at)}` : ''}`
              : 'Default wording'}
          </p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div className="max-w-xs">
          <label className={LABEL} htmlFor={`${badgeKey}-word`}>Pop-up word</label>
          <input id={`${badgeKey}-word`} value={word} onChange={(e) => setWord(e.target.value)} maxLength={WORD_MAX + 10}
            className={`${INPUT} ${word.length > WORD_MAX ? 'border-rose-400' : ''}`} />
          <p className={`mt-1 text-[11px] ${word.length > WORD_MAX ? 'text-rose-600' : 'text-gray-400'}`}>
            {word.length}/{WORD_MAX} — pops up in big letters on the phone
          </p>
        </div>

        <div>
          <p className={LABEL}>Messages</p>
          <ol className="space-y-3">
            {messages.map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i} className="flex gap-2">
                <span className="mt-2 w-5 shrink-0 text-right text-xs font-semibold text-gray-400">{i + 1}.</span>
                <div className="min-w-0 flex-1">
                  <textarea value={m} onChange={(e) => setMessage(i, e.target.value)} rows={2}
                    className={`${INPUT} resize-y ${m.length > MESSAGE_MAX ? 'border-rose-400' : ''}`} />
                  <div className="mt-1 flex flex-wrap justify-between gap-2 text-[11px]">
                    <span className="italic text-gray-500">{m.trim() ? `“${preview(m.trim())}”` : 'Empty — left out when saved'}</span>
                    <span className={m.length > MESSAGE_MAX ? 'text-rose-600' : 'text-gray-400'}>{m.length}/{MESSAGE_MAX}</span>
                  </div>
                </div>
                <button type="button" onClick={() => setMessages((x) => x.filter((_, j) => j !== i))}
                  disabled={messages.length <= 1} title="Remove this message"
                  className="mt-1.5 h-8 w-8 shrink-0 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30">
                  <XMarkIcon className="mx-auto h-4 w-4" />
                </button>
              </li>
            ))}
          </ol>
          {messages.length < MESSAGES_MAX ? (
            <button type="button" onClick={() => setMessages((x) => [...x, ''])}
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline">
              <PlusIcon className="h-4 w-4" /> Add a message
            </button>
          ) : null}
        </div>

        {save.isError ? <ErrorNote error={save.error} /> : null}
        {reset.isError ? <ErrorNote error={reset.error} fallback="That did not reset." /> : null}

        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!changed || tooLong || empty || save.isPending}
            onClick={() => save.mutate({ key: badgeKey, word, messages })} className={BTN}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {changed ? (
            <button type="button" onClick={() => { setWord(data.word); setMessages(data.messages); }} className={BTN_SECONDARY}>
              Undo changes
            </button>
          ) : null}
          {data.customised ? (
            <button type="button" disabled={reset.isPending}
              onClick={() => window.confirm(`Put ${title} back to the default wording?`) && reset.mutate({ key: badgeKey })}
              className={BTN_SECONDARY}>
              Reset to default wording
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function BadgeMessagesTab() {
  const q = useAdminBadgeMessages();
  return (
    <div className="space-y-4">
      <Card className="px-4 py-3 text-sm text-gray-600">
        <p>
          When someone earns a badge, the FSM app plays a celebration: the badge, a word that pops up, and a message
          under it. Each badge stands for one of Nano&apos;s values, so keep its word and messages to that value.
        </p>
        <p className="mt-2">
          Give each badge a few messages. Everyone who earns it is dealt one at random, and it stays theirs. Write
          {' '}<code className="rounded bg-gray-100 px-1 text-xs">{'{name}'}</code>{' '}
          where their first name should go — the previews use “{SAMPLE_NAME}”. Changes reach the app straight away.
        </p>
      </Card>
      <Loading q={q} what="the badge messages" />
      {q.data ? BADGES.map(([key, title, how]) => (q.data[key] ? (
        <BadgeMessages key={key} badgeKey={key} title={title} how={how} data={q.data[key]} />
      ) : null)) : null}
    </div>
  );
}

export default BadgeMessagesTab;
